import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reportsApi, type ReportFilters } from '../api/reports'
import { clientsApi } from '../api/clients'
import { formatDateBR, formatDateTimeBR } from '../lib/format'
import type { ReportListItem, ReportStatus } from '../lib/types'
import { Spinner } from '../components/Spinner'

export default function Reports() {
  const [filters, setFilters] = useState<ReportFilters>({ read: 'unread' })

  const { data: clientsData } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list() })
  const { data, isLoading } = useQuery({
    queryKey: ['reports', filters],
    queryFn: () => reportsApi.list(filters),
    refetchInterval: (query) =>
      query.state.data?.reports.some((r) => r.status === 'processing') ? 3000 : false,
  })

  const clients = clientsData?.clients ?? []
  const reports = data?.reports ?? []

  // Agrupa por cliente, preservando a ordem do backend (cliente alfabético,
  // dentro do cliente data decrescente).
  const groups: { id: string; name: string; reports: ReportListItem[] }[] = []
  const idx = new Map<string, number>()
  for (const r of reports) {
    let i = idx.get(r.client.id)
    if (i === undefined) {
      i = groups.length
      idx.set(r.client.id, i)
      groups.push({ id: r.client.id, name: r.client.name, reports: [] })
    }
    groups[i].reports.push(r)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-slate-900">Relatórios</h1>

      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filters.clientId ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, clientId: e.target.value || undefined }))}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            <option value="">Todos os clientes</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={filters.read ?? 'unread'}
            onChange={(e) =>
              setFilters((f) => ({ ...f, read: e.target.value as ReportFilters['read'] }))
            }
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            <option value="unread">Não lidos</option>
            <option value="read">Lidos</option>
            <option value="all">Todos</option>
          </select>

          <select
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: (e.target.value || undefined) as ReportFilters['status'] }))
            }
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            <option value="">Todos os status</option>
            <option value="draft">Rascunho</option>
            <option value="published">Publicado</option>
          </select>

          <input
            type="date"
            value={filters.week ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, week: e.target.value || undefined }))}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
          {filters.week && (
            <button
              onClick={() => setFilters((f) => ({ ...f, week: undefined }))}
              className="text-xs text-slate-500 hover:underline"
            >
              limpar data
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          A data filtra pela <strong>semana de Segunda a Domingo</strong> que contém o dia escolhido.
        </p>
      </div>

      {isLoading ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum relatório encontrado.
        </p>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.id} className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-900">
                {g.name}
              </div>
              <table className="w-full min-w-[560px] text-sm">
                <tbody className="divide-y divide-slate-100">
                  {g.reports.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <Link to={`/reports/${r.id}`} className="font-medium text-slate-800 hover:text-brand-600">
                          {formatDateBR(r.weekStart)} a {formatDateBR(r.weekEnd)}
                        </Link>
                        <div className="mt-0.5 flex gap-1.5">
                          {!r.isRead && (
                            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                              Não lido
                            </span>
                          )}
                          {r.hasPendingHighActions && (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                              Ação urgente
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{formatDateTimeBR(r.generatedAt)}</td>
                      <td className="px-4 py-2.5">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Link to={`/reports/${r.id}`} className="text-sm text-brand-600 hover:underline">
                          Abrir →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: ReportStatus }) {
  const map: Record<ReportStatus, { label: string; cls: string }> = {
    processing: { label: 'Processando…', cls: 'bg-amber-100 text-amber-700' },
    draft: { label: 'Rascunho', cls: 'bg-slate-100 text-slate-600' },
    published: { label: 'Publicado', cls: 'bg-green-100 text-green-700' },
    error: { label: 'Erro', cls: 'bg-red-100 text-red-700' },
  }
  const { label, cls } = map[status]
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>
}
